/**
 * The copilot's proposals (contract v1, 2026-09-26): how the page applies each kind, who may approve it, what its card
 * says, and what an approval, a rejection or a save came to. The brain changes nothing: a person approves a proposal on
 * its card, and their browser applies it through the platform's own create routes, as them, with their permissions,
 * then records the outcome on the proposal (`applied` and `rejected` are final; `failed` is not, so the person can try
 * again). Pure, with the calls injected, so the container in routes/twin/imports-copilot.tsx only runs what these
 * decide (the older in-browser copilot's idea, lib/copilot/tools.ts: check a proposal before its card offers to apply
 * it, and say why not).
 *
 * Kinds: correlation_group and alarm_rule are made through their create routes; a report opens the Reports page,
 * which generates it; a process window and a scrap target (the plant's limits, 2026-09-27) are set through the limits
 * routes, shown on the Plant limits page; a dashboard widget (2026-09-28) is a trend added to a board the org has, or
 * to a new board made for it, through the dashboards routes (copilot-widget), shown on that board; an Acquisition
 * study's decisions (acquisition_change, P3c) are saved as the person's own through PUT .../decisions by an org admin,
 * only those that still apply (lib/acquisition/consultant), shown on the study's page.
 *
 * Units (units slice 2d, docs/plans/2026-10-06_units-slice-2.md): an alarm's threshold and a window's ends are held in
 * the unit the spec names, which may be the unit the person reads and gave them in ("°F"), never converted by the
 * brain. Approve converts them to the unit the tag or the parameter is stored in (CardUnits) before it calls the routes,
 * which take that unit alone; a card with no unit, or in the stored unit, is sent exactly as it holds it, as before.
 */
import { ApiClientError } from "@/lib/api-client";
import type {
  CopilotMessage,
  CopilotProposalBlock,
  CopilotProposalKind,
  CopilotProposalOutcome,
  CopilotProposalResult,
  CopilotProposalState,
  CopilotProposalStatus,
} from "@/lib/api/copilot-imports";
import type { CorrelationGroup, CorrelationGroupInput } from "@/lib/api/twin-correlation-groups";
import type { AlarmRuleCreateBody, AlarmRuleDTO } from "@/lib/api/types";
import type {
  LimitBasis,
  ProcessWindow,
  ProcessWindowParameter,
  ProcessWindowWrite,
  ScrapTarget,
  ScrapTargetWrite,
} from "@/lib/api/limits";
import { limitsFoldOf, limitsHref, windowText } from "@/lib/brief/limits";
import { dimensionOf, normalise } from "@/lib/units/convert";
import { shownFor, typedToStored } from "@/lib/units/display";
import type { DisplayPrefs } from "@/lib/units/prefs";
import { pct } from "@/lib/brief/words";
import { dayNumber, formatDayRange, isPlantDay } from "@/lib/plant-days";
import { reportUrl, reportViewUrl } from "@/lib/reports/report-link";
import { GRANULARITIES } from "@/lib/reports/statistics";
import type { AcquisitionDecisionIn } from "@/lib/api/acquisition";
import { changesOf, changesSummary, decisionOf } from "@/lib/acquisition/consultant";
import { studyHref } from "@/lib/acquisition/review";
import { asSentence, refusalOf, type Refusal } from "./copilot";
import { amountText, isObject, proposalResultOf } from "./copilot-blocks";
import {
  DASHBOARDS_URL,
  TREND_RANGES,
  WidgetRefused,
  dashboardUrl,
  type TrendRange,
  type WidgetPlan,
} from "./copilot-widget";
import { boardPlanOf, boardText, isBoardSpec, type BoardPlan } from "./copilot-board";

/** Where each applied kind is shown (the contract's result urls). */
export const CORRELATION_GROUPS_URL = "/app/twin/correlations?tab=groups";
export const ALARM_RULES_URL = "/app/alarms?tab=rules";

export const PROPOSAL_KIND_LABEL: Record<CopilotProposalKind, string> = {
  correlation_group: "Correlation group",
  alarm_rule: "Alarm rule",
  report: "Report",
  dashboard_widget: "Dashboard widget",
  process_window: "Process window",
  scrap_target: "Scrap target",
  acquisition_change: "Study decisions",
};

/** A proposal's kind on its card: a dashboard_widget card that holds a board (v2) is a dashboard, not one widget. */
export function proposalKindLabel(block: Pick<CopilotProposalBlock, "kind" | "spec">): string {
  return block.kind === "dashboard_widget" && isBoardSpec(block.spec) ? "Dashboard" : PROPOSAL_KIND_LABEL[block.kind];
}

/**
 * The permission each kind's create route checks (orgctx: the group routes gate on a member's write, model.write; an
 * Acquisition study's decisions on an org admin's).
 */
const PERMISSION: Record<CopilotProposalKind, { perm: string; refused: string } | null> = {
  correlation_group: { perm: "model.write", refused: "Your role cannot add correlation groups." },
  alarm_rule: { perm: "alarms.write", refused: "Your role cannot add alarm rules." },
  report: null, // opening a report changes nothing
  dashboard_widget: { perm: "dashboards.write", refused: "Your role cannot add dashboard widgets." },
  process_window: { perm: "model.write", refused: "Your role cannot set the plant's limits." },
  scrap_target: { perm: "model.write", refused: "Your role cannot set the plant's limits." },
  acquisition_change: { perm: "org.admin", refused: "An org admin applies a study's decisions." },
};

/** Where a set limit shows: the Plant limits page (at the window's row). */
export const LIMITS_URL = "/app/twin/limits";

/**
 * How the page applies a proposal: a create route with its body (and where the result shows), a widget for a board
 * (made with the board when the board is new), or a page to open.
 */
export type ApplyPlan =
  | { kind: "correlation_group"; body: CorrelationGroupInput; url: string }
  | { kind: "alarm_rule"; body: AlarmRuleCreateBody; url: string }
  | { kind: "process_window"; body: ProcessWindowWrite; url: string }
  | { kind: "scrap_target"; body: ScrapTargetWrite; url: string }
  | WidgetPlan
  | BoardPlan
  | {
      kind: "acquisition_change";
      studyId: string;
      decisions: AcquisitionDecisionIn[];
      url: string;
      /** What applying did and skipped, in words, once the page has judged each change: recorded with the outcome. */
      summary?: string;
    }
  | { kind: "report"; url: string };

/** A plan made through a create route (a widget's through the dashboards routes). */
export type CreatePlan = Exclude<ApplyPlan, { kind: "report" }>;

/** A set limit's id: the input and grade a window is for, or the defect (or the total) a target is for. */
export function limitIdOf(plan: Extract<CreatePlan, { kind: "process_window" | "scrap_target" }>): string {
  return plan.kind === "process_window"
    ? `${plan.body.condition}${plan.body.grade ? `|${plan.body.grade}` : ""}`
    : plan.body.kind === "total"
      ? "total"
      : (plan.body.defect ?? "");
}

export type PlanCheck = { ok: true; plan: ApplyPlan } | { ok: false; reason: string };

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SEVERITIES = new Set(["info", "warning", "critical"]);
const STATUSES = new Set(["proposed", "applied", "failed", "rejected"]);
const KINDS = new Set([
  "correlation_group",
  "alarm_rule",
  "report",
  "dashboard_widget",
  "process_window",
  "scrap_target",
  "acquisition_change",
]);

function trimmed(v: unknown): string {
  return typeof v === "string" ? v.trim() : "";
}

/** A list of names, each trimmed, blanks dropped; [] when it is not a list. */
function names(v: unknown): string[] {
  return Array.isArray(v) ? v.map(trimmed).filter((s) => s !== "") : [];
}

/** A limit set from a card records the card as its basis (migration 0193), where the card names its proposal. */
function cardBasis(block: CopilotProposalBlock): { basis?: LimitBasis } {
  return block.proposal_id && UUID.test(block.proposal_id) ? { basis: { kind: "consultant", proposal_id: block.proposal_id } } : {};
}

/**
 * What the page knows of the units a card's numbers are stored in (units slice 2d). Each lookup answers a unit, null
 * where the page has read its list and it holds no such tag or parameter, or undefined while the list is not read.
 */
export interface CardUnits {
  /** The org's own choice of display units (useDisplayUnits().shown); undefined: every value is read as stored. */
  prefs?: DisplayPrefs;
  /** An alarm's tag's unit ("" for a tag with none), from the org's tag list. */
  tagUnit?: (tagId: string) => string | null | undefined;
  /** The unit the plant records a window's parameter in, from the Plant limits page's list of parameters. */
  conditionUnit?: (condition: string) => string | null | undefined;
}

/**
 * The lookups of CardUnits from what the page has read (pure; the container's hooks read the lists): the org's tags by
 * id (undefined while not read), and the parameters the plant records (undefined while not read; null where the
 * platform serves no list), each found by its own name or by a name the plant records it under, as a window's save
 * folds it (limitsFoldOf).
 */
export function cardUnitsOf(read: {
  tags?: Record<string, TagWords>;
  parameters?: readonly ProcessWindowParameter[] | null;
}): Required<Pick<CardUnits, "tagUnit" | "conditionUnit">> {
  const { tags, parameters } = read;
  const units = new Map((parameters ?? []).map((p) => [p.condition, p.unit] as const));
  const fold = limitsFoldOf(parameters ?? []);
  return {
    tagUnit: (id) => (tags === undefined ? undefined : tags[id] ? (tags[id].unit ?? "") : null),
    conditionUnit: (condition) => {
      if (parameters === undefined) return undefined;
      return units.get(condition) ?? units.get(fold(condition)) ?? null;
    },
  };
}

/**
 * A window's unit that may be one the org reads, not the stored one (units slice 2d): a unit the table knows, in an org
 * with a choice that does not convert it away. A unit the choice converts (°C for a US org) is never one the org reads,
 * so a card in it is in the stored unit, as every card was before.
 */
function mayBeShown(unit: string, prefs: DisplayPrefs | undefined): boolean {
  return Boolean(unit) && prefs !== undefined && dimensionOf(unit) !== "" && !shownFor(unit, prefs).converted;
}

/**
 * Which list the page reads before Approve sets a card's numbers in the unit they are stored in (units slice 2d): the
 * org's tags for an alarm whose spec names a unit (without them it is never sent), the parameters the plant records
 * for a window in a unit the org may read (without them it goes as it holds its ends: windowInStored); null for any
 * other card, which goes as it holds its numbers.
 */
export function needsStoredUnit(block: CopilotProposalBlock, prefs: DisplayPrefs | undefined): "tag" | "condition" | null {
  const spec = block.spec as unknown as Record<string, unknown>;
  if (block.kind === "alarm_rule") return trimmed(spec.unit) ? "tag" : null;
  if (block.kind === "process_window") return mayBeShown(trimmed(spec.unit), prefs) ? "condition" : null;
  return null;
}

/** A number of a card in the unit it is stored in: the same number where it is already, or why it cannot be sent. */
type InStored = { ok: true; values: number[]; unit: string; converted: boolean } | { ok: false; reason: string };

/**
 * A card's numbers, held in `unit`, in the unit they are stored in (units slice 2d): converted where the two differ
 * and convert (typedToStored, the same table the server converts by), the same numbers exactly where they are one unit
 * in any spelling. `what` words the refusal where they do not convert.
 */
function toStored(values: number[], unit: string, stored: string, what: (stored: string) => string): InStored {
  if (normalise(unit) === normalise(stored)) return { ok: true, values, unit, converted: false };
  const out = values.map((v) => typedToStored(v, unit, stored));
  if (out.some((v) => v === null)) return { ok: false, reason: what(stored) };
  return { ok: true, values: out as number[], unit: stored, converted: true };
}

/**
 * An alarm's threshold as the alarm route takes it, in its tag's unit: as the card holds it where the spec names no
 * unit (the tag's, as before); else converted from the spec's unit to the tag's, which the page must know first.
 */
function alarmInStored(tagId: string, value: number, unit: string, units: CardUnits): InStored {
  if (!unit) return { ok: true, values: [value], unit, converted: false };
  const stored = units.tagUnit?.(tagId);
  if (stored === undefined) return { ok: false, reason: "Reading the alarm's tag, to set the threshold in the unit the tag records." };
  if (stored === null) {
    return { ok: false, reason: `The alarm's tag is not one this page knows, so its threshold, in ${unit}, cannot be put in the unit the tag records.` };
  }
  return toStored([value], unit, stored, (s) =>
    s
      ? `The threshold is in ${unit}, which does not convert to ${s}, the unit the tag records: it cannot be set from this card.`
      : `The threshold is in ${unit}, and the tag records no unit to convert it to: it cannot be set from this card.`,
  );
}

/**
 * A window's ends as the limits route takes them, in the unit the plant records the parameter in: where the page knows
 * that unit, converted to it from the spec's. Where it does not (the list not read, a parameter it does not hold, a
 * parameter with no unit), they go as the card holds them, as every card did before 2d: the limits route refuses a
 * unit that is not the parameter's own (unit_mismatch), so a window is never stored in another. The page reads the
 * list before it applies a card that needs it (needsStoredUnit), so a card in the person's unit is converted.
 */
function windowInStored(condition: string, ends: number[], unit: string, units: CardUnits): InStored {
  const stored = unit ? units.conditionUnit?.(condition) : undefined;
  if (typeof stored !== "string" || stored.trim() === "") return { ok: true, values: ends, unit, converted: false };
  return toStored(ends, unit, stored, (s) =>
    `The window's ends are in ${unit}, which does not convert to ${s}, the unit the plant records ${condition} in: it cannot be set from this card.`,
  );
}

/**
 * How the page applies a proposal, checked as the contract states each spec (the server checked it before storing,
 * so a failure here is rare; the card then says why, with Approve off). An alarm's threshold and a window's ends go in
 * the unit they are stored in (units slice 2d: `units`, alarmInStored, windowInStored).
 */
export function planApply(block: CopilotProposalBlock, units: CardUnits = {}): PlanCheck {
  const spec = block.spec as unknown as Record<string, unknown>;
  switch (block.kind) {
    case "correlation_group": {
      const name = trimmed(spec.name);
      const inputs = names(spec.input_params);
      const outcomes = names(spec.outcome_defects);
      if (!name) return { ok: false, reason: "The proposed group has no name." };
      if ([...name].length > 80) return { ok: false, reason: "The proposed group's name is too long." };
      if (inputs.length < 1 || inputs.length > 12) return { ok: false, reason: "The proposed group needs 1 to 12 inputs." };
      if (outcomes.length < 1 || outcomes.length > 8) return { ok: false, reason: "The proposed group needs 1 to 8 defects." };
      return {
        ok: true,
        plan: {
          kind: "correlation_group",
          body: { name, mechanism: trimmed(spec.mechanism), input_params: inputs, outcome_defects: outcomes },
          url: CORRELATION_GROUPS_URL,
        },
      };
    }
    case "alarm_rule": {
      const name = trimmed(spec.name);
      const tagId = trimmed(spec.tag_id);
      const op = spec.op;
      const value = spec.value;
      if (!name) return { ok: false, reason: "The proposed alarm has no name." };
      if (!UUID.test(tagId)) return { ok: false, reason: "The proposed alarm names no tag." };
      if ((op !== "gt" && op !== "lt") || typeof value !== "number" || !Number.isFinite(value)) {
        return { ok: false, reason: "The proposed alarm has no condition." };
      }
      if (typeof spec.severity !== "string" || !SEVERITIES.has(spec.severity)) {
        return { ok: false, reason: "The proposed alarm has no severity." };
      }
      const stored = alarmInStored(tagId, value, trimmed(spec.unit), units);
      if (!stored.ok) return stored;
      return {
        ok: true,
        plan: {
          kind: "alarm_rule",
          body: {
            name,
            target_tag_id: tagId,
            condition: { op, value: stored.values[0] },
            severity: spec.severity as AlarmRuleCreateBody["severity"],
            enabled: true,
          },
          url: ALARM_RULES_URL,
        },
      };
    }
    case "process_window": {
      const condition = trimmed(spec.condition);
      const grade = trimmed(spec.grade);
      const end = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : undefined);
      const lo = end(spec.lo);
      const hi = end(spec.hi);
      if (!condition) return { ok: false, reason: "The proposed window names no input." };
      if (lo === undefined && hi === undefined) return { ok: false, reason: "The proposed window has no end." };
      if (lo !== undefined && hi !== undefined && !(lo < hi)) {
        return { ok: false, reason: "The proposed window's low end is above its high end." };
      }
      const held = [lo, hi].filter((v): v is number => v !== undefined);
      const stored = windowInStored(condition, held, trimmed(spec.unit), units);
      if (!stored.ok) return stored;
      const [first, second] = stored.values;
      const ends = lo !== undefined && hi !== undefined ? { lo: first, hi: second } : lo !== undefined ? { lo: first } : { hi: first };
      const unit = stored.unit;
      const note = trimmed(spec.note);
      const body: ProcessWindowWrite = {
        condition,
        ...(grade ? { grade } : {}),
        ...ends,
        ...(unit ? { unit } : {}),
        ...(note ? { note } : {}),
        ...cardBasis(block),
      };
      return { ok: true, plan: { kind: "process_window", body, url: limitsHref(condition, grade ? `grade:${grade}` : undefined) } };
    }
    case "scrap_target": {
      const rate = spec.target_rate;
      const defect = trimmed(spec.defect);
      if (spec.kind !== "total" && (spec.kind !== "defect" || !defect)) {
        return { ok: false, reason: "The proposed target names no defect." };
      }
      if (typeof rate !== "number" || !Number.isFinite(rate) || rate < 0 || rate > 1) {
        return { ok: false, reason: "The proposed target is not a rate from 0% to 100%." };
      }
      const note = trimmed(spec.note);
      const body: ScrapTargetWrite =
        spec.kind === "total"
          ? { kind: "total", target_rate: rate, ...(note ? { note } : {}), ...cardBasis(block) }
          : { kind: "defect", defect, target_rate: rate, ...(note ? { note } : {}), ...cardBasis(block) };
      return { ok: true, plan: { kind: "scrap_target", body, url: LIMITS_URL } };
    }
    case "report": {
      const g = spec.granularity;
      if (g !== "daily" && g !== "weekly" && g !== "monthly") return { ok: false, reason: "The proposed report has no grouping." };
      if (!isPlantDay(spec.from) || !isPlantDay(spec.to)) return { ok: false, reason: "The proposed report has no dates." };
      if (spec.from > spec.to) return { ok: false, reason: "The proposed report starts after it ends." };
      if (dayNumber(spec.to) - dayNumber(spec.from) > 400) return { ok: false, reason: "The proposed report covers more than 400 days." };
      return { ok: true, plan: { kind: "report", url: reportUrl({ granularity: g, from: spec.from, to: spec.to }) } };
    }
    case "dashboard_widget": {
      // A board of widgets (v2, copilot-board), or a trend (v1).
      if (isBoardSpec(spec)) return boardPlanOf(spec);
      // A trend on the board the spec names by id, or on a new board it names (dashboard_id null): the name is for a
      // new board only, and the id decides when both are there.
      const w = isObject(spec.widget) ? spec.widget : null;
      if (!w || w.kind !== "trend") return { ok: false, reason: "The proposed widget is not a trend." };
      const title = trimmed(w.title);
      if (!title) return { ok: false, reason: "The proposed widget has no title." };
      if ([...title].length > 80) return { ok: false, reason: "The proposed widget's title is too long." };
      const tagIds = Array.isArray(w.tag_ids) ? w.tag_ids.map((t) => trimmed(t).toLowerCase()) : [];
      if (tagIds.length < 1 || tagIds.length > 4) return { ok: false, reason: "The proposed widget needs 1 to 4 tags." };
      if (!tagIds.every((t) => UUID.test(t))) return { ok: false, reason: "One of the proposed widget's tags is not a tag id." };
      if (new Set(tagIds).size < tagIds.length) return { ok: false, reason: "The proposed widget names a tag twice." };
      const range = w.range;
      if (typeof range !== "string" || !(TREND_RANGES as readonly string[]).includes(range)) {
        return { ok: false, reason: "The proposed widget has no time range." };
      }
      const widget = { title, tagIds, range: range as TrendRange };
      if (spec.dashboard_id === null || spec.dashboard_id === undefined) {
        const name = trimmed(spec.dashboard_name);
        if (!name) return { ok: false, reason: "The proposed widget's new board has no name." };
        if ([...name].length > 80) return { ok: false, reason: "The proposed board's name is too long." };
        return { ok: true, plan: { kind: "dashboard_widget", board: { name }, widget, url: DASHBOARDS_URL } };
      }
      const id = trimmed(spec.dashboard_id).toLowerCase();
      if (!UUID.test(id)) return { ok: false, reason: "The proposed widget names no dashboard." };
      return { ok: true, plan: { kind: "dashboard_widget", board: { id }, widget, url: dashboardUrl(id) } };
    }
    case "acquisition_change": {
      // Every change the card holds, as PUT .../decisions takes it: applying it sends only those that still apply to
      // the study as it then stands (lib/acquisition/consultant changeApplyPlan).
      const studyId = trimmed(spec.study_id).toLowerCase();
      if (!UUID.test(studyId)) return { ok: false, reason: "The proposed decisions name no study." };
      const changes = changesOf(spec.changes);
      if (!changes) return { ok: false, reason: "The proposed decisions do not read: ask the consultant again." };
      return {
        ok: true,
        plan: { kind: "acquisition_change", studyId, decisions: changes.map(decisionOf), url: studyHref(studyId) },
      };
    }
    default:
      return { ok: false, reason: "The page cannot apply this kind of proposal." };
  }
}

/** A proposal the server stored without its id cannot have its outcome recorded. */
const NO_ID = "This proposal cannot be decided here. Reload the page.";

/**
 * Why Approve is off, or null: the proposal has no id to record against, the page cannot apply it (planApply, in the
 * units it knows), or the person's role may not make it (as the page its kind belongs to gates its own writes).
 */
export function approveBlocked(block: CopilotProposalBlock, can: (perm: string) => boolean, units: CardUnits = {}): string | null {
  if (!block.proposal_id) return NO_ID;
  const plan = planApply(block, units);
  if (!plan.ok) return plan.reason;
  const gate = PERMISSION[block.kind];
  return gate && !can(gate.perm) ? gate.refused : null;
}

const RANGE_WORDS: Record<string, string> = { "24h": "24 hours", "7d": "7 days", "30d": "30 days", "1y": "a year" };

function capitalised(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}

/** A tag's name and unit, when the page knows them (the org's tag list); the card never waits for them. */
export interface TagWords {
  name: string;
  unit?: string;
}

/**
 * A proposal's spec in words, for its card: a group's inputs → outcomes, by the spec's own names (the server stores
 * them canonical; the page never folds them again); an alarm's tag, "above/below value" and severity (the tag named
 * once the page knows it; the title names it meanwhile); a report's grouping and dates. Never throws on a spec that
 * is not well formed: it says what it can.
 *
 * The values are SHOWN in the org's units (units slice 2b): an alarm's threshold is in the spec's unit, else its tag's,
 * and a window's ends in the spec's own unit. A card held in a unit other than the one its numbers are stored in (units
 * slice 2d: the person's own figure) also says what Approve saves them as, where the page knows that unit (`units`).
 */
export function proposalSpecText(
  block: CopilotProposalBlock,
  tag?: TagWords | null,
  prefs?: DisplayPrefs,
  units: CardUnits = {},
): string {
  const spec = block.spec as unknown as Record<string, unknown>;
  switch (block.kind) {
    case "correlation_group":
      return `${names(spec.input_params).join(", ")} → ${names(spec.outcome_defects).join(", ")}`;
    case "alarm_rule": {
      const side = spec.op === "gt" ? "above" : spec.op === "lt" ? "below" : null;
      const value = typeof spec.value === "number" && Number.isFinite(spec.value) ? spec.value : null;
      const own = trimmed(spec.unit);
      let when = "";
      if (side && value !== null) {
        const held = own || (tag?.unit ?? "");
        const s = shownFor(held, prefs);
        const amount = s.converted ? amountText(Number(s.convert(value).toPrecision(6)), s.unit) : amountText(value, held || undefined);
        const stored = own && typeof spec.tag_id === "string" ? alarmInStored(spec.tag_id, value, own, units) : null;
        const saved = stored?.ok && stored.converted ? ` (saved as ${amountText(Number(stored.values[0].toPrecision(4)), stored.unit)})` : "";
        when = tag ? `${tag.name} ${side} ${amount}${saved}` : own ? `${capitalised(side)} ${amount}${saved}` : `${capitalised(side)} ${value}`;
      }
      else if (tag) when = tag.name;
      const severity = typeof spec.severity === "string" && SEVERITIES.has(spec.severity) ? capitalised(spec.severity) : "";
      return [when, severity].filter(Boolean).join(" · ");
    }
    case "report": {
      const def = GRANULARITIES.find((d) => d.key === spec.granularity && d.key !== "heat");
      const what = def ? `${def.label} report` : "Report";
      return isPlantDay(spec.from) && isPlantDay(spec.to) && spec.from <= spec.to
        ? `${what} · ${formatDayRange(spec.from, spec.to)}`
        : what;
    }
    case "process_window": {
      const condition = trimmed(spec.condition);
      const grade = trimmed(spec.grade);
      const end = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : undefined);
      const lo = end(spec.lo);
      const hi = end(spec.hi);
      const unit = trimmed(spec.unit);
      const ends = windowText({ lo, hi }, unit, prefs);
      const held = [lo, hi].filter((v): v is number => v !== undefined);
      const stored = condition && held.length > 0 ? windowInStored(condition, held, unit, units) : null;
      const [first, second] = stored?.ok ? stored.values : [];
      const savedEnds = lo !== undefined && hi !== undefined ? { lo: first, hi: second } : lo !== undefined ? { lo: first } : { hi: first };
      const saved = stored?.ok && stored.converted ? ` (saved as ${windowText(savedEnds, stored.unit)})` : "";
      const what = `${condition}${grade ? `, grade ${grade}` : ""}`;
      return ends ? `${what}: ${ends}${saved}` : what;
    }
    case "scrap_target": {
      const what = spec.kind === "total" ? "All scrap" : trimmed(spec.defect);
      const rate = spec.target_rate;
      return typeof rate === "number" && Number.isFinite(rate) ? `${what}: ${pct(rate)} of castings` : what;
    }
    case "dashboard_widget": {
      if (isBoardSpec(spec)) {
        // A board: its widgets of each kind and their titles, and the new board it makes.
        const plan = boardPlanOf(spec);
        if (!plan.ok) return "A board";
        let out = `${boardText(plan.plan.widgets)} (${plan.plan.widgets.map((w) => w.title).join(", ")})`;
        if ("name" in plan.plan.board) out += `, on a new board: ${plan.plan.board.name}`;
        return out;
      }
      const widget = isObject(spec.widget) ? spec.widget : null;
      if (!widget) return "Trend";
      const count = Array.isArray(widget.tag_ids) ? widget.tag_ids.length : 0;
      const range = typeof widget.range === "string" ? RANGE_WORDS[widget.range] : undefined;
      let out = count > 0 ? `Trend of ${count} tag${count === 1 ? "" : "s"}` : "Trend";
      if (range) out += ` over ${range}`;
      const board = trimmed(spec.dashboard_name);
      if (spec.dashboard_id === null && board) out += `, on a new board: ${board}`;
      return out;
    }
    case "acquisition_change": {
      const changes = changesOf(spec.changes);
      return changes ? changesSummary(changes) : "";
    }
    default:
      return "";
  }
}

/** An app path the card may link to ("/app/…", at most 300 characters, as the outcome route takes it), or null. */
export function appPath(url: unknown): string | null {
  if (typeof url !== "string" || url.length > 300 || url.includes("\\")) return null;
  return url === "/app" || url.startsWith("/app/") || url.startsWith("/app?") ? url : null;
}

/**
 * What this page is doing with a proposal, beside what the server holds: an action running, the reason the last one
 * did not go (which one: apply, reject, or saving a made change), and a change made whose outcome the thread has not
 * taken yet (it is saved again, never applied again).
 */
export interface ProposalLocal {
  busy: "approve" | "reject" | "save" | null;
  error: Refusal | null;
  failed: "apply" | "reject" | "save" | null;
  unrecorded: CopilotProposalResult | null;
}

export const IDLE_PROPOSAL: ProposalLocal = { busy: null, error: null, failed: null, unrecorded: null };

export interface ProposalButton {
  label: string;
  disabled: boolean;
  busy: boolean;
}

/** Everything a proposal's card shows. */
export interface ProposalView {
  state: CopilotProposalStatus;
  /** Applied or rejected on the server: the card shows it, with no buttons. */
  final: boolean;
  /** Why it failed, or why this page's last action did not go. */
  error: Refusal | null;
  /** Where what it made shows (an app path). */
  link: string | null;
  approve: ProposalButton | null;
  reject: ProposalButton | null;
  /** Records a change already made: shown only while the thread has not taken it. */
  save: ProposalButton | null;
  /** Why Approve is off, or that a made change is not yet in the thread. */
  note: string | null;
}

/**
 * A proposal's card: the server's state, and this page's. Applied and rejected are final: no buttons, whatever this
 * page holds. A change made but not recorded shows as applied, offering to save it. Otherwise Approve (Try again once
 * an apply failed) and Reject, both held while an action runs; Approve is off with the reason (`blocked`,
 * approveBlocked), and Reject stays.
 */
export function proposalView(block: CopilotProposalBlock, local: ProposalLocal, blocked: string | null): ProposalView {
  const server = block.status ?? "proposed";
  if (server === "applied" || server === "rejected") {
    return {
      state: server,
      final: true,
      error: null,
      link: server === "applied" ? appPath(block.result?.url) : null,
      approve: null,
      reject: null,
      save: null,
      note: null,
    };
  }
  const busy = local.busy !== null;
  if (local.unrecorded) {
    return {
      state: "applied",
      final: false,
      error: local.error,
      link: appPath(local.unrecorded.url),
      approve: null,
      reject: null,
      save: { label: "Save to the conversation", disabled: busy, busy: local.busy === "save" },
      note: "It was made, but the conversation has not recorded it yet.",
    };
  }
  const failed = server === "failed" || local.failed === "apply";
  const recorded = server === "failed" ? { text: block.error || "It could not be applied.", code: null } : null;
  return {
    state: failed ? "failed" : "proposed",
    final: false,
    error: local.error ?? recorded,
    link: null,
    approve: { label: failed ? "Try again" : "Approve", disabled: busy || blocked !== null, busy: local.busy === "approve" },
    reject: { label: "Reject", disabled: busy || !block.proposal_id, busy: local.busy === "reject" },
    save: null,
    note: blocked,
  };
}

/**
 * The thread with a proposal's recorded state merged into its block. The page polls only for new messages, so it
 * patches the block itself once an outcome is recorded. Other messages keep their identity; a proposal the thread
 * does not hold changes nothing.
 */
export function withProposalState(items: CopilotMessage[], state: CopilotProposalState): CopilotMessage[] {
  let changed = false;
  const out = items.map((m) => {
    if (!Array.isArray(m.blocks)) return m;
    const i = m.blocks.findIndex((b) => isObject(b) && b.type === "proposal" && b.proposal_id === state.proposal_id);
    if (i === -1) return m;
    changed = true;
    const blocks = [...m.blocks];
    blocks[i] = {
      ...(blocks[i] as CopilotProposalBlock),
      status: state.status,
      result: state.result ?? null,
      error: state.error ?? null,
      decided_at: state.decided_at ?? null,
    };
    return { ...m, blocks };
  });
  return changed ? out : items;
}

/** The outcome route's answer ({proposal}) as a proposal's state; what was sent, when the answer says too little. */
export function proposalStateOf(res: unknown, fallback: CopilotProposalState): CopilotProposalState {
  const p = isObject(res) && isObject(res.proposal) ? res.proposal : null;
  if (!p || typeof p.status !== "string" || !STATUSES.has(p.status)) return fallback;
  return {
    proposal_id: typeof p.proposal_id === "string" && p.proposal_id ? p.proposal_id : fallback.proposal_id,
    kind: typeof p.kind === "string" && KINDS.has(p.kind) ? (p.kind as CopilotProposalKind) : fallback.kind,
    status: p.status as CopilotProposalStatus,
    result: isObject(p.result) ? proposalResultOf(p.result) : null,
    error: typeof p.error === "string" ? p.error : null,
    decided_at: typeof p.decided_at === "string" ? p.decided_at : null,
  };
}

function codeOf(err: ApiClientError): string | null {
  const body = isObject(err.body) ? err.body : null;
  return typeof body?.error === "string" && body.error ? body.error : null;
}

function detailOf(err: ApiClientError): string | null {
  const body = isObject(err.body) ? err.body : null;
  const said = [body?.detail, body?.message].find((w): w is string => typeof w === "string" && w.trim() !== "");
  return said ?? null;
}

/**
 * Why a create route refused a change, in words: the platform's own words as a sentence, or the page's own sentence
 * with the server's code shown small (a missing tag or dashboard, a role that may not write, a failure of the
 * platform). A request that got no answer is said as the conversation says it (refusalOf).
 */
export function changeRefusal(err: unknown): Refusal {
  if (err instanceof ApiClientError && err.status < 500) {
    const code = codeOf(err);
    if (err.status === 403) return { text: "Your role cannot make this change.", code: code ?? "HTTP 403" };
    if (err.status === 404 && !detailOf(err)) {
      if (code === "tag_not_found") return { text: "The alarm's tag was not found in this org.", code };
      if (code === "dashboard_not_found") return { text: "The dashboard was not found in this org.", code };
      return { text: "The platform could not find what this change needs.", code: code ?? "HTTP 404" };
    }
    if (err.status === 404) return { text: asSentence(detailOf(err) as string), code: null };
  }
  return refusalOf(err, "The platform refused the change.");
}

/** A failure as recorded on the proposal, for the brain and the thread: its words, and its code. */
export function failureRecord(error: Refusal): string {
  return error.code ? `${error.text} (${error.code})` : error.text;
}

function sameNames(a: unknown, b: string[]): boolean {
  const x = new Set(names(a));
  const y = new Set(names(b));
  return x.size === y.size && [...x].every((s) => y.has(s));
}

/** A custom group the create made: the same name, inputs and outcomes (the server trims each, and keeps case). */
export function sameGroup(g: CorrelationGroup, body: CorrelationGroupInput): boolean {
  return !g.is_standard && trimmed(g.name) === trimmed(body.name) && sameNames(g.input_params, body.input_params) &&
    sameNames(g.outcome_defects, body.outcome_defects);
}

/**
 * The open window a set made: the same input and grade, with the same ends. fold keys both names as the platform keys a
 * window's save (limitsFoldOf: a name the plant records a parameter under is the parameter's own), so a proposal naming
 * such a name finds the window the platform set under the parameter's.
 */
export function sameWindow(w: ProcessWindow, body: ProcessWindowWrite, fold: (name: string) => string = (n) => n): boolean {
  return (
    !w.superseded_at &&
    fold(w.condition) === fold(body.condition) &&
    (w.grade ?? "") === (body.grade ?? "") &&
    w.lo === body.lo &&
    w.hi === body.hi
  );
}

/** The open target a set made: the same defect (or the total), at the same rate. */
export function sameTarget(t: ScrapTarget, body: ScrapTargetWrite): boolean {
  return (
    !t.superseded_at &&
    t.kind === body.kind &&
    (t.defect ?? "") === (body.defect ?? "") &&
    t.target_rate === body.target_rate
  );
}

/** A rule the create made: the same name, tag, condition and severity. */
export function sameRule(r: AlarmRuleDTO, body: AlarmRuleCreateBody): boolean {
  const c = isObject(r.condition) ? r.condition : null;
  const want = body.condition;
  if (!c || want.op === "spc" || c.op !== want.op || Number(c.value) !== want.value) return false;
  return trimmed(r.name) === trimmed(body.name) && r.target_tag_id === body.target_tag_id && r.severity === body.severity;
}

/** What an approval, a rejection or a save came to. The container sets the card and the thread from it. */
export type ActionResult =
  /** The thread took the outcome: its state patches the thread. `error`: an apply failure it recorded. */
  | { kind: "recorded"; proposal: CopilotProposalState; error: Refusal | null }
  /** The proposal was decided elsewhere (409): the page reads the thread again. */
  | { kind: "decided" }
  /** The change was made and the thread did not take its outcome: saved again, never applied again. */
  | { kind: "unrecorded"; result: CopilotProposalResult; error: Refusal }
  /** It did not go, and nothing new was recorded: the card says why. */
  | { kind: "failed"; error: Refusal };

/** The calls an action makes, injected so the decisions are tested without a platform. */
export interface ProposalDeps {
  /**
   * The proposal's status as the server holds it now (the thread read again), or null when the thread no longer
   * holds it: another tab, or the same person elsewhere, may have decided it since this page read the thread.
   */
  current: () => Promise<CopilotProposalStatus | null>;
  /** The create route for a plan, as the person: the id of what it made (a widget: its board's). */
  create: (plan: CreatePlan) => Promise<{ id: string }>;
  /** After a create that got no answer: the id of the change the platform holds (a widget: its board's), or null. */
  find: (plan: CreatePlan) => Promise<string | null>;
  /** Records the outcome on the proposal: its state as the server now holds it. */
  record: (outcome: CopilotProposalOutcome) => Promise<CopilotProposalState>;
  /** Opens an app path (a report). */
  open: (url: string) => void;
}

type Recorded = { kind: "recorded"; proposal: CopilotProposalState } | { kind: "decided" } | { kind: "unsaved"; error: Refusal };

async function recordAs(outcome: CopilotProposalOutcome, deps: ProposalDeps): Promise<Recorded> {
  try {
    return { kind: "recorded", proposal: await deps.record(outcome) };
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 409) return { kind: "decided" };
    return { kind: "unsaved", error: refusalOf(err, "The conversation could not record this.") };
  }
}

function afterApplied(out: Recorded, result: CopilotProposalResult): ActionResult {
  if (out.kind === "recorded") return { kind: "recorded", proposal: out.proposal, error: null };
  if (out.kind === "decided") return out;
  return { kind: "unrecorded", result, error: out.error };
}

const UNCONFIRMED: Record<CreatePlan["kind"], string> = {
  correlation_group: "We could not confirm the correlation group was made. Check the correlation groups before trying again.",
  alarm_rule: "We could not confirm the alarm rule was made. Check the alarm rules before trying again.",
  process_window: "We could not confirm the window was set. Check the Plant limits page before trying again.",
  scrap_target: "We could not confirm the target was set. Check the Plant limits page before trying again.",
  dashboard_widget: "We could not confirm the widget was added. Check the dashboards before trying again.",
  acquisition_change: "We could not confirm the decisions were saved. Check the study before trying again.",
};

/**
 * Makes a plan's change: the id of what it made, or why not. A create that got no answer from the server (the
 * connection dropped, or its answer could not be read) may still have made the change, so the page looks for it
 * before it says the change failed, and never makes it again by itself (as a message that lost its answer is looked
 * for, #868). When the page cannot look, it says it could not confirm, and to check before trying again. A widget the
 * page refused before it sent anything (a tag it cannot bind) is said as it is: there is nothing to look for.
 */
async function makeChange(plan: CreatePlan, deps: ProposalDeps): Promise<{ id: string } | { error: Refusal }> {
  try {
    return { id: (await deps.create(plan)).id };
  } catch (err) {
    if (err instanceof ApiClientError) return { error: changeRefusal(err) };
    if (err instanceof WidgetRefused) return { error: err.refusal };
    try {
      const id = await deps.find(plan);
      return id !== null ? { id } : { error: changeRefusal(err) };
    } catch {
      return { error: { text: UNCONFIRMED[plan.kind], code: null } };
    }
  }
}

/** The thread no longer holds the proposal (its message was deleted, or the page is out of date). */
const GONE = "This proposal is no longer in the conversation. Reload the page.";

/**
 * What an applied change made, as the proposal records it: its id, and where it shows. A widget has no page of its
 * own, so it records its board, whose page a new board's id gives only once the board is made.
 */
function appliedResult(plan: CreatePlan, id: string): CopilotProposalResult {
  const url = plan.kind === "dashboard_widget" ? dashboardUrl(id) : plan.url;
  // An Acquisition card applies only the rows still undecided: the outcome keeps how many it applied and skipped.
  return plan.kind === "acquisition_change" && plan.summary ? { id, url, summary: plan.summary } : { id, url };
}

/**
 * Approves a proposal: applies its plan as the person, then records the outcome.
 *   - A report: records applied with its link, then opens it (the page generates it), whether or not the record went.
 *     Opening a report twice changes nothing, so it reads nothing first.
 *   - A change: first the proposal's live state (another tab, or the same person elsewhere, may have decided it:
 *     then nothing is applied and the page reads the thread again), then whether the change is already there (made
 *     by another tab whose outcome is not recorded yet: it is recorded, not made twice), then the create.
 *   - A change made: records applied with its id and link (a widget: its board's id and page); if the record does not
 *     go, the change is kept to be saved.
 *   - A change refused or lost: records failed with why, the first time (a proposal already failed records nothing
 *     new until a success); the card shows why, with Try again.
 */
export async function approveProposal(plan: ApplyPlan, deps: ProposalDeps): Promise<ActionResult> {
  if (plan.kind === "report") {
    // The card keeps the view link (no generate step), so reopening it shows the report rather than generating a copy.
    const result = { url: reportViewUrl(plan.url) };
    const out = await recordAs({ status: "applied", result }, deps);
    deps.open(plan.url);
    return afterApplied(out, result);
  }
  let status: CopilotProposalStatus | null;
  try {
    status = await deps.current();
  } catch (err) {
    return { kind: "failed", error: refusalOf(err, "The proposal's state could not be read.") };
  }
  if (status === "applied" || status === "rejected") return { kind: "decided" };
  if (status === null) return { kind: "failed", error: { text: GONE, code: null } };
  let existing: string | null = null;
  try {
    existing = await deps.find(plan);
  } catch {
    // The look is a guard for another tab's change: when the list cannot be read, the create's own answer decides.
  }
  if (existing !== null) return saveApplied(appliedResult(plan, existing), deps);
  const made = await makeChange(plan, deps);
  if ("id" in made) return saveApplied(appliedResult(plan, made.id), deps);
  if (status !== "proposed") return { kind: "failed", error: made.error };
  const out = await recordAs({ status: "failed", error: failureRecord(made.error) }, deps);
  if (out.kind === "recorded") return { kind: "recorded", proposal: out.proposal, error: made.error };
  if (out.kind === "decided") return out;
  return { kind: "failed", error: made.error };
}

/** Records a change already made as applied (never makes it again). */
export async function saveApplied(result: CopilotProposalResult, deps: ProposalDeps): Promise<ActionResult> {
  return afterApplied(await recordAs({ status: "applied", result }, deps), result);
}

/** Rejects a proposal, with the person's reason when they gave one (L2): final once recorded. */
export async function rejectProposal(deps: ProposalDeps, reason = ""): Promise<ActionResult> {
  const why = reason.trim();
  const out = await recordAs(why ? { status: "rejected", reason: why } : { status: "rejected" }, deps);
  if (out.kind === "recorded") return { kind: "recorded", proposal: out.proposal, error: null };
  if (out.kind === "decided") return out;
  return { kind: "failed", error: out.error };
}

/** The card's own state while an action runs: a made change's result stays while it is saved again. */
export function localBusy(prev: ProposalLocal | undefined, action: "approve" | "reject" | "save"): ProposalLocal {
  return { busy: action, error: null, failed: null, unrecorded: prev?.unrecorded ?? null };
}

/** The card's own state after an action: none once the thread shows the outcome; else what the thread cannot show. */
export function localAfter(action: "approve" | "reject" | "save", r: ActionResult): ProposalLocal | null {
  switch (r.kind) {
    case "recorded":
      return r.error ? { busy: null, error: r.error, failed: "apply", unrecorded: null } : null;
    case "decided":
      return null;
    case "unrecorded":
      return { busy: null, error: r.error, failed: "save", unrecorded: r.result };
    case "failed":
      return { busy: null, error: r.error, failed: action === "approve" ? "apply" : action, unrecorded: null };
  }
}
